import React, { useState } from 'react';

import { useLocation } from 'react-router-dom';

import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { formatScopeCriteria } from '~/components/GHADiscounting/utils';

import { useCugLeakSummary } from '~/hooks/apis/connection-google/useCugLeakSummary';
import { useGHADiscountSummary } from '~/hooks/apis/connection-google/useGHADiscountSummary';
import useAppNavigation from '~/hooks/useAppNavigation';

import type {
  GHAAccountCugLeakSummary,
  GHAAccountDiscountSummary,
  GHACoverageBuckets,
  GHADiscountSummaryLine,
  GHAPropertyClassificationTier,
} from '~/services/ConnectionGoogleService';

import CugLeakingSection from './CugLeakingSection';
import SectionLabel from './SectionLabel';
import { ACCOUNT_LABELS, CLASSIFICATION_TIER_LABELS, CLASSIFICATION_TIER_OPTIONS } from './constants';
import {
  MergedDiscountLine,
  coveragePercent,
  formatCountry,
  formatDiscountKind,
  formatExclusionRule,
  formatExclusionRuleShort,
  formatRuleCoordinates,
  isClassificationTier,
  mergeDiscountLines,
  tierEntries,
} from './utils';

function TierChips({ byTier, size = 'small' }: { byTier: Record<string, number>; size?: 'small' | 'medium' }) {
  const entries = tierEntries(byTier);
  if (entries.length === 0) {
    return null;
  }
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {entries.map(([tier, label, count]) => (
        <Chip key={tier} size={size} variant="outlined" label={`${label} · ${count.toLocaleString()}`} />
      ))}
    </Stack>
  );
}

// One color per mechanism so "which kind of discount" reads at a glance across the whole page —
// the same visual language the GHA-429 rule grid will use for grants.
function DiscountKindChip({ line }: { line: GHADiscountSummaryLine }) {
  return (
    <Chip
      size="small"
      color={line.discountType === 'margin_retention' ? 'info' : 'success'}
      label={formatDiscountKind(line)}
    />
  );
}

function CoverageBar({ propertyCount, liveTotal }: { propertyCount: number; liveTotal: number }) {
  const percent = coveragePercent(propertyCount, liveTotal);
  if (percent == null) {
    return null;
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <LinearProgress variant="determinate" value={percent} sx={{ width: 90, height: 6, borderRadius: 3 }} />
      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {percent}%
      </Typography>
    </Stack>
  );
}

// GHA-429: every live property in exactly one bucket (resolver-precedence order) — rendered only
// once the expanded summary endpoint is deployed and the response carries `coverage`.
function CoverageStrip({ coverage }: { coverage: GHACoverageBuckets }) {
  // Every chip is a property count; the six buckets partition the live estate exactly. Buckets are
  // per property but the Active discounts lines are per country, so a property whose rule names only
  // some countries sits in the property-rule bucket AND on the scope-rule lines for the rest. A
  // scope-rule line exceeding the scope-rule chip is therefore expected, not a mismatch.
  const buckets: Array<{
    label: string;
    hint: string;
    value: number;
    color?: 'default' | 'warning' | 'success' | 'info';
  }> = [
    {
      label: 'blacklisted',
      hint: 'Property is discount-blacklisted. Never discounted on any country.',
      value: coverage.blacklisted,
      color: 'warning',
    },
    {
      label: 'fully excluded',
      hint: 'An exclusion with no supplier or country qualifier matches the property, so every leg is blocked.',
      value: coverage.fullyExcluded,
      color: 'warning',
    },
    {
      label: 'discounted by property rule',
      hint: 'Has an enabled property rule. Countries the rule does not name still fall through to scope rules, so these properties also appear on scope-rule lines below.',
      value: coverage.propertyRuleDiscounted,
      color: 'success',
    },
    {
      label: 'discounted by scope rules only',
      hint: 'No property rule; at least one scope rule matches. Smaller than the scope-rule lines below, which also count property-rule properties for the countries their rule leaves out.',
      value: coverage.scopeRuleDiscounted,
      color: 'info',
    },
    {
      label: 'without any discount',
      hint: 'No property rule and no matching scope rule.',
      value: coverage.undiscounted,
    },
  ];
  return (
    <Box sx={{ mt: 2 }}>
      <SectionLabel>Property coverage</SectionLabel>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
        <Tooltip title="Every live property lands in exactly one bucket, in the order the discount resolver applies them: blacklist, then unconditional exclusions, then the property's own rule, then scope rules.">
          <Chip size="small" label={`${coverage.live.toLocaleString()} live properties`} />
        </Tooltip>
        {buckets.map((bucket) => (
          <Tooltip key={bucket.label} title={bucket.hint}>
            <Chip
              size="small"
              variant="outlined"
              color={bucket.color}
              label={`${bucket.value.toLocaleString()} ${bucket.label}`}
            />
          </Tooltip>
        ))}
      </Stack>
    </Box>
  );
}

function SourceCell({ line, scopedRulesLive }: { line: MergedDiscountLine; scopedRulesLive?: boolean }) {
  const location = useLocation();
  const { push } = useAppNavigation();
  if (line.scoped == null) {
    return <Typography variant="body2">Property rule</Typography>;
  }
  const openRule = () => {
    const params = new URLSearchParams(location.search);
    params.set('section', 'scope-rules');
    params.set('rule', line.scoped?.ruleId ?? '');
    push({ search: params.toString() });
  };
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Chip
        size="small"
        color="info"
        variant="outlined"
        label={`Scope rule · #${line.scoped.priority}`}
        onClick={openRule}
      />
      {!scopedRulesLive && (
        <Tooltip title="The rule is configured, but scope rules are not emitting yet — nothing changes on Google until scope-rule emission is switched on.">
          <Chip size="small" color="warning" label="not live" />
        </Tooltip>
      )}
    </Stack>
  );
}

function AccountHeader({ account }: { account: GHAAccountDiscountSummary }) {
  const accountLabel = ACCOUNT_LABELS[account.accountType] ?? account.accountType;
  return (
    <Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" useFlexGap>
      <Typography variant="h6">{accountLabel}</Typography>
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {account.liveProperties.total.toLocaleString()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          live properties
        </Typography>
      </Stack>
    </Stack>
  );
}

function AccountCard({
  account,
  cugLeaking,
  onOpenExclusion,
}: {
  account: GHAAccountDiscountSummary;
  cugLeaking?: GHAAccountCugLeakSummary;
  onOpenExclusion?: (ruleId: string) => void;
}) {
  const mergedLines = mergeDiscountLines(account);
  const hasDiscounts = mergedLines.length > 0;
  const hasBlocks = account.exclusionRules.length > 0;

  return (
    <Card variant="outlined">
      <CardContent>
        <AccountHeader account={account} />
        {account.ruleCoordinates.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Rule eligibility: {formatRuleCoordinates(account.ruleCoordinates)}
          </Typography>
        )}
        <Box sx={{ mt: 1.5 }}>
          <TierChips byTier={account.liveProperties.byTier} />
        </Box>
        {account.coverage != null && <CoverageStrip coverage={account.coverage} />}

        {/* Slim card for accounts with nothing to report — no empty sections. */}
        {!hasDiscounts && !hasBlocks ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No active discounts.
          </Typography>
        ) : (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionLabel>Active discounts</SectionLabel>
            <Typography variant="caption" color="text.secondary" display="block">
              Counts are per viewer country — a property appears on every country line it is discounted in, so lines
              don't sum to the coverage figures above. For a specific property's outcome, use the property's page.
            </Typography>
            {hasDiscounts ? (
              <Table size="small" sx={{ mt: 0.5, tableLayout: 'fixed', '& td, & th': { py: 1 } }}>
                <TableHead>
                  <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                    <TableCell sx={{ width: '8%' }}>Country</TableCell>
                    <TableCell sx={{ width: '12%' }}>Discount</TableCell>
                    <TableCell sx={{ width: '11%' }}>Source</TableCell>
                    <TableCell sx={{ width: '21%' }}>
                      <Tooltip title="The scope rule's criteria. A rule spanning several countries appears under each country it grants — never sum the counts down this table.">
                        <span>Scope</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ width: '10%' }}>
                      Live properties
                    </TableCell>
                    <TableCell sx={{ width: '12%', pl: 4 }}>
                      <Tooltip title="Share of the account's live properties this line covers. Supplier-scoped blocks (below) can still suppress the discount on those suppliers' rates.">
                        <span>% with rule</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ width: '26%' }}>Tier breakdown</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mergedLines.map((line) => (
                    <TableRow key={line.key} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCountry(line.country)}</TableCell>
                      <TableCell>
                        <DiscountKindChip line={line} />
                      </TableCell>
                      <TableCell>
                        <SourceCell line={line} scopedRulesLive={account.scopedRulesLive} />
                      </TableCell>
                      <TableCell>
                        {line.scoped == null ? (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            {formatScopeCriteria({ ...line.scoped.scope, userCountries: null })}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {line.propertyCount.toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ pl: 4 }}>
                        <CoverageBar propertyCount={line.propertyCount} liveTotal={account.liveProperties.total} />
                      </TableCell>
                      <TableCell>
                        <TierChips byTier={line.byTier} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                No active discounts.
              </Typography>
            )}

            {hasBlocks && (
              <Box sx={{ mt: 2 }}>
                <SectionLabel>Blocked from discounting</SectionLabel>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {account.exclusionRules.map((line, index) => (
                    <Tooltip key={line.id ?? index} title={formatExclusionRule(line)}>
                      <Chip
                        size="small"
                        variant="outlined"
                        color="warning"
                        icon={<BlockIcon />}
                        label={
                          line.affectedLiveProperties == null
                            ? formatExclusionRuleShort(line)
                            : `${formatExclusionRuleShort(line)} · ${line.affectedLiveProperties.toLocaleString()}`
                        }
                        // Rows with an id come from the exclusion table and link through to
                        // the Exclusions tab; config-era lines have no id and stay inert.
                        onClick={line.id == null ? undefined : () => onOpenExclusion?.(line.id)}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            )}

            {cugLeaking != null && <CugLeakingSection account={cugLeaking} />}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function GHADiscountSummaryTable() {
  const location = useLocation();
  const { push } = useAppNavigation();
  const [classificationTiers, setClassificationTiers] = useState<Array<GHAPropertyClassificationTier>>([]);

  // Link-through into the Exclusions sub-tab of the GHA Discounting page this table renders under.
  const openExclusion = (ruleId: string) => {
    const params = new URLSearchParams(location.search);
    params.set('section', 'exclusions');
    params.set('rule', ruleId);
    push({ search: params.toString() });
  };
  const filters = {
    classificationTiers: classificationTiers.length === 0 ? undefined : classificationTiers,
  };
  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useGHADiscountSummary(filters);
  // Separate endpoint, so leaking renders when it arrives and its failure cannot break this page.
  const {
    data: cugLeakSummaries,
    isFetching: isFetchingCugLeaking,
    refetch: refetchCugLeaking,
  } = useCugLeakSummary(filters);

  if (isLoading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading discount summary...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">Failed to load the GHA discount summary</Alert>
      </Box>
    );
  }

  // Accounts with nothing discounted (LPC, and ALT until it launches) are hidden entirely
  // rather than rendered as empty tiles.
  const accounts = (data ?? []).filter(
    (account) =>
      account.discounts.length > 0 || account.exclusionRules.length > 0 || (account.scopedDiscounts ?? []).length > 0,
  );
  if (accounts.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No accounts with active discounts.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 6 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1.5}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="gha-discount-summary-tier">Tier</InputLabel>
            <Select
              labelId="gha-discount-summary-tier"
              label="Tier"
              multiple
              value={classificationTiers}
              onChange={(event) => {
                const values = Array.isArray(event.target.value) ? event.target.value : [event.target.value];
                if (values.includes('all')) {
                  setClassificationTiers([]);
                  return;
                }
                setClassificationTiers(values.filter(isClassificationTier));
              }}
              renderValue={(selected) => selected.map((value) => CLASSIFICATION_TIER_LABELS[value]).join(', ')}
            >
              <MenuItem value="all">All</MenuItem>
              {CLASSIFICATION_TIER_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {CLASSIFICATION_TIER_LABELS[option]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="caption" color="text.secondary">
            As of {new Date(dataUpdatedAt).toLocaleTimeString()}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              refetch();
              refetchCugLeaking();
            }}
            disabled={isFetching || isFetchingCugLeaking}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>
      <Stack spacing={2}>
        {accounts.map((account) => (
          <AccountCard
            key={account.accountType}
            account={account}
            cugLeaking={(cugLeakSummaries ?? []).find((entry) => entry.accountType === account.accountType)}
            onOpenExclusion={openExclusion}
          />
        ))}
      </Stack>
    </Box>
  );
}
